import * as t from 'io-ts';
import { DateFromISOString, fallback } from 'io-ts-types';
import { PathReporter } from 'io-ts/lib/PathReporter';
import captureError from './captureError';
import undefinedToFalseCodec from './undefinedToFalseCodec';

const BaseResult = t.type(
  {
    CountryCode: t.union([t.undefined, t.string]),
    Highlight: t.string,
    Id: t.union([t.undefined, t.number]),
    Link: t.string,
    // LinkPath: t.union([t.undefined, t.string]),
    Logo: t.string,
    Score: t.number,
  },
  'BaseResult'
);

export const CharityResult = t.intersection(
  [
    BaseResult,
    t.type({
      Description: t.string,
      Name: t.string,
      ProfileWhat: t.union([t.undefined, t.string]),
      ProfileWhy: t.union([t.undefined, t.string]),
      Type: t.literal('Charity'),
    }),
  ],
  'Charity'
);
export type CharityResult = t.TypeOf<typeof CharityResult>;

export const TeamResult = t.intersection(
  [
    BaseResult,
    t.type({
      CharityName: t.string,
      MemberCount: t.number,
      Name: t.string,
      Type: t.literal('teamsv1'),
    }),
  ],
  'Team'
);
export type TeamResult = t.TypeOf<typeof TeamResult>;

export const CrowdfundingResult = t.intersection(
  [
    BaseResult,
    t.type({
      Amount: t.number,
      Currency: t.string,
      OwnerFullName: t.string,
      PitchFor: t.string,
      Type: t.literal('LocalProject'),
    }),
  ],
  'Crowdfunding'
);
export type CrowdfundingResult = t.TypeOf<typeof CrowdfundingResult>;

export const CampaignResult = t.intersection(
  [
    BaseResult,
    t.type({
      CampaignGuid: t.union([t.undefined, t.string]),
      CharityId: t.union([t.undefined, t.number]),
      CharityName: t.string,
      Name: t.string,
      Subtext: t.union([t.undefined, t.string]),
      DateExpired: t.union([t.undefined, DateFromISOString]),
      IsExpired: undefinedToFalseCodec,
      Type: t.literal('Campaign'),
    }),
  ],
  'Campaign'
);
export type CampaignResult = t.TypeOf<typeof CampaignResult>;

export const FundraiserResult = t.intersection(
  [
    BaseResult,
    t.type({
      CharityId: t.union([t.undefined, t.number]),
      Name: t.string,
      OwnerFullName: t.string,
      Type: t.literal('Fundraiser'),
      Subtext: t.union([t.undefined, t.string]),
    }),
  ],
  'Fundraiser'
);
export type FundraiserResult = t.TypeOf<typeof FundraiserResult>;

export const PageResult = t.intersection(
  [
    BaseResult,
    t.type({
      Name: t.string,
      Description: t.union([t.undefined, t.string]),
      OwnerFullName: t.string,
      Type: t.literal('Page'),
      ProductName: t.string,
    }),
  ],
  'Page'
);

export type PageResult = t.TypeOf<typeof PageResult>;

const BaseGroupedResults = t.type(
  {
    Count: t.number,
    Title: t.string,
    TitleKey: t.string,

    // Used by the app, not from the API
    PageNumber: fallback(t.number)(1),
  },
  'BaseGroupedResults'
);

export const CharitiesGroupedResults = t.intersection(
  [
    BaseGroupedResults,
    t.type({
      Results: t.array(CharityResult, 'Results'),
      Specific: t.literal('charity'),
    }),
  ],
  'CharitiesGroupedResults'
);
export type CharitiesGroupedResults = t.TypeOf<typeof CharitiesGroupedResults>;

export const TeamsGroupedResults = t.intersection(
  [
    BaseGroupedResults,
    t.type({
      Results: t.array(TeamResult, 'Results'),
      Specific: t.literal('teamsv1'),
    }),
  ],
  'TeamsGroupedResults'
);
export type TeamsGroupedResults = t.TypeOf<typeof CharitiesGroupedResults>;

export const CrowdfundingGroupedResults = t.intersection(
  [
    BaseGroupedResults,
    t.type({
      Results: t.array(CrowdfundingResult, 'Results'),
      Specific: t.literal('localproject'),
    }),
  ],
  'CrowdfundingGroupedResults'
);
export type CrowdfundingGroupedResults = t.TypeOf<
  typeof CrowdfundingGroupedResults
>;

export const CampaignGroupedResults = t.intersection(
  [
    BaseGroupedResults,
    t.type({
      Results: t.array(CampaignResult, 'Results'),
      Specific: t.literal('campaign'),
    }),
  ],
  'CampaignGroupedResults'
);
export type CampaignGroupedResults = t.TypeOf<typeof CampaignGroupedResults>;

export const FundraiserGroupedResults = t.intersection(
  [
    BaseGroupedResults,
    t.type({
      Results: t.array(FundraiserResult, 'Results'),
      Specific: t.literal('fundraiser'),
    }),
  ],
  'FundraiserGroupedResults'
);
export type FundraiserGroupedResults = t.TypeOf<
  typeof FundraiserGroupedResults
>;

export const PageGroupedResults = t.intersection(
  [
    BaseGroupedResults,
    t.type({
      Results: t.array(PageResult, 'Results'),
      Specific: t.literal('page'),
    }),
  ],
  'PageGroupedResults'
);
export type PageGroupedResults = t.TypeOf<typeof PageGroupedResults>;

export const GroupedResults = t.taggedUnion(
  'Specific',
  [
    CampaignGroupedResults,
    CharitiesGroupedResults,
    CrowdfundingGroupedResults,
    FundraiserGroupedResults,
    TeamsGroupedResults,
    PageGroupedResults,
  ],
  'GroupedResults'
);
export type GroupedResults = t.TypeOf<typeof GroupedResults>;

// Infer the Result union type used to help TS out later on
export type Result = GroupedResults['Results'] extends (infer T)[] ? T : never;

export const Suggestion = t.type(
  {
    Text: t.string,
    Score: t.number,
  },
  'Suggestion'
);
export type Suggestion = t.TypeOf<typeof Suggestion>;

const NonNegativeNumber = t.refinement(
  t.number,
  x => x >= 0,
  'NonNegativeNumber'
);

function searchResultsWithGroupedResultsType<C extends t.Mixed>(
  groupedResultsType: C,
  name: string
) {
  return t.type(
    {
      Country: t.union([t.undefined, t.string]),
      GroupedResults: t.array(groupedResultsType, 'GroupedResults'),
      Limit: NonNegativeNumber,
      Query: t.string,
      SpecificIndex: t.string,
      Suggestions: t.array(Suggestion, 'Suggestions'),
      Total: fallback(NonNegativeNumber)(0),
    },
    name
  );
}

// Codec for search results with no validation of GroupedResults.
// This will be used to make sure the outer structure of the results is valid
const SearchResultsWithAnyGroupedResults = searchResultsWithGroupedResultsType(
  t.unknown,
  'SearchResultsWithAnyGroupedResults'
);

// Codec for search results with full GroupedResults validation
// Represents completely valid results with known GroupedResults
export const SearchResults = searchResultsWithGroupedResultsType(
  GroupedResults,
  'SearchResults'
);
export type SearchResults = t.TypeOf<typeof SearchResults>;

// Codec for validating the outer results structure, then filtering out
// any GroupedResults that don’t conform, resulting in a valid SearchResults
// structure
const SearchResultsFilter = new t.Type<SearchResults, SearchResults, unknown>(
  'SearchResults',
  SearchResults.is,
  (i, context) =>
    SearchResultsWithAnyGroupedResults.validate(i, context).map(results => {
      const newGroupedResults: GroupedResults[] = [];

      // If we get a failure on any of these result groups we’ll log an error
      const failuresToLog: GroupedResults['Specific'][] = [
        'campaign',
        'charity',
        'fundraiser',
        'localproject',
        'teamsv1',
        'page'
      ];

      for (const gr of results.GroupedResults) {
        const grDecodeResult = GroupedResults.decode(gr);

        if (grDecodeResult.isRight()) {
          newGroupedResults.push(grDecodeResult.value);
          continue;
        }

        if (
          typeof gr === 'object' &&
          gr &&
          failuresToLog.includes((gr as GroupedResults).Specific)
        ) {
          captureError(
            new Error(
              `Failed to decode search result group: ${PathReporter.report(
                grDecodeResult
              ).join('\n')}`
            ),
            { input: gr }
          );
        }
      }

      return { ...results, GroupedResults: newGroupedResults };
    }),
  t.identity
);

export function decodeSearchResults(input: unknown): SearchResults {
  const decodeResult = SearchResultsFilter.decode(input);

  if (decodeResult.isLeft()) {
    throw new Error(PathReporter.report(decodeResult).join('\n'));
  }

  return decodeResult.value;
}
